import React, { useState, useEffect, useCallback } from 'react';
import { Zap } from 'lucide-react';
import { API_URL } from '../../config';

// This component existed twice, 157 lines in MetaPerformance and 154 in
// GooglePerformance. The only differences were three JSX comments and the
// brand accent colour, which is now the `accent` prop.
const AIAnalysisPanel = ({ clientId, clientName, isOpen, onClose, accent = '#6366f1' }) => {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);

  const fetchAnalysis = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/ai/analyze-client?client_id=${clientId}`, {
        method: 'POST'
      });
      if (!response.ok) throw new Error('Analysis failed');
      const data = await response.json();
      setAnalysis(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [clientId]);

  useEffect(() => {
    if (isOpen && clientId) {
      fetchAnalysis();
    }
  }, [isOpen, clientId, fetchAnalysis]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#09090B] rounded-2xl border border-white/10 max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#6366f1]" />
              AI Analysis
            </h2>
            <p className="text-sm text-zinc-500 mt-1">{clientName}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div
                className="w-12 h-12 border-4 rounded-full animate-spin"
                style={{ borderColor: `${accent}4D`, borderTopColor: accent }}
              ></div>
              <p className="text-zinc-400 mt-4">Analyzing performance data...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
              <p className="text-red-400">Failed to analyze: {error}</p>
              <button onClick={fetchAnalysis} className="mt-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm">
                Retry
              </button>
            </div>
          )}

          {analysis && !loading && (
            <div className="space-y-6">
              {/* Summary */}
              {analysis.summary && (
                <div
                  className="border rounded-xl p-4"
                  style={{ backgroundColor: `${accent}1A`, borderColor: `${accent}4D` }}
                >
                  <p className="text-white font-medium">{analysis.summary}</p>
                </div>
              )}

              {/* Problems */}
              {analysis.problems && analysis.problems.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-red-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Problems Identified
                  </h3>
                  <div className="space-y-3">
                    {analysis.problems.map((problem, idx) => (
                      <div key={idx} className={`rounded-xl p-4 border ${
                        problem.severity === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                        problem.severity === 'warning' ? 'bg-amber-500/10 border-amber-500/30' :
                        'bg-blue-500/10 border-blue-500/30'
                      }`}>
                        <div className="flex items-start gap-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                            problem.severity === 'critical' ? 'bg-red-500/30 text-red-400' :
                            problem.severity === 'warning' ? 'bg-amber-500/30 text-amber-400' :
                            'bg-blue-500/30 text-blue-400'
                          }`}>
                            {problem.severity}
                          </span>
                          <div>
                            <h4 className="font-bold text-white">{problem.title}</h4>
                            <p className="text-sm text-zinc-400 mt-1">{problem.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Solutions */}
              {analysis.solutions && analysis.solutions.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-emerald-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Recommended Solutions
                  </h3>
                  <div className="space-y-3">
                    {analysis.solutions.map((solution, idx) => (
                      <div key={idx} className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                            solution.priority === 'high' ? 'bg-emerald-500/30 text-emerald-400' :
                            solution.priority === 'medium' ? 'bg-amber-500/30 text-amber-400' :
                            'bg-zinc-500/30 text-zinc-400'
                          }`}>
                            {solution.priority}
                          </span>
                          <div>
                            <p className="text-sm text-zinc-300">{solution.action}</p>
                            {solution.for_problem && (
                              <p className="text-xs text-zinc-500 mt-1">Solves: {solution.for_problem}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!analysis.problems || analysis.problems.length === 0) && (!analysis.solutions || analysis.solutions.length === 0) && (
                <div className="text-center py-8">
                  <svg className="w-10 h-10 text-[#22c55e] mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <p className="text-white font-medium mt-2">Account is performing well!</p>
                  <p className="text-zinc-500 text-sm">No major issues detected.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIAnalysisPanel;
